import { forwardRef, ReactNode } from 'react';
import cn from 'classnames';

import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Icon, { type IconName } from 'components/icon/Icon';

import { ITextInput } from 'source/NFR.types';

const themes = defineTheme({
  hiddenLabel: 'text-input--theme-hidden-label',
  inverted: 'text-input--theme-inverted',
  small: 'text-input--theme-small',
  rounded: 'text-input--theme-rounded'
});
type Theme = ThemeKeys<typeof themes>;

const ifWrapWithIcon = (input: ReactNode, icon: IconName | '', fill: boolean) => {
  if (!icon) return input;
  return (
    <div className="text-input--input-wrapper">
      <Icon name={icon} fill={fill} /> {input}
    </div>
  );
};

interface TextInputProps extends ITextInput {
  className?: string;
  controlled?: boolean;
  idPrefix?: string;
  iconFill?: boolean;
  theme?: Theme | Theme[];
  attributes?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  icon?: IconName | '';
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    attributes,
    className,
    controlled,
    idPrefix = '',
    label,
    name,
    onChange = () => {},
    placeholder,
    theme,
    type = 'text',
    validationError,
    value,
    icon = '',
    iconFill = false
  } = props;

  const id = `input-${idPrefix}-${name}`;
  const valueProp = controlled ? { value } : { defaultValue: value };

  return (
    <div className={cn('text-input', className, themes(theme))}>
      {label && <label htmlFor={id}>{label}</label>}
      {validationError && <div className="text-input--validation-error">{validationError}</div>}
      {ifWrapWithIcon(
        <input
          id={id}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          ref={ref}
          type={type}
          {...valueProp}
          {...attributes}
        />,
        icon,
        iconFill
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;
